import { Form, Input } from "antd"
import dayjs from "dayjs"
import { keyBy } from "lodash"
import { motion } from "motion/react"
import { useEffect } from "react"

import { classAtoms, classSessionAtoms } from "#atoms"
import AnimateInFlex, { ANIMATION_BY_KEY } from "#blocks/AnimateInFlex"
import Flex from "#blocks/Flex"
import { TRYOUT_FEES } from "#constants/tryoutOptions"
import { useThemeContext } from "#contexts/ThemeContextProvider"
import { ClassSessionConstructor } from "#customTypes/composedTypes/classSessionTypes"
import { ClassConstructor } from "#customTypes/composedTypes/classTypes"
import { DOC_TYPES } from "#customTypes/composedTypes/firebaseTypes"
import { StudentConstructor } from "#customTypes/composedTypes/studentTypes"
import { Uuid } from "#customTypes/globalTypes"
import { useOneDoc } from "#hooks/jotai/useSubscription"
import { getSessionInfoFromId } from "#utils/classHelpers"
import {
  formatDob,
  STANDARD_DATE_FORMAT,
  UNKNOWN_DOB,
} from "#utils/dateHelpers"

const feesById = keyBy(TRYOUT_FEES, "id")

const ReceiptItem = ({ label, value }: { label: string; value: string }) => {
  return (
    <Flex col gap={4}>
      <motion.h3 {...ANIMATION_BY_KEY.leftSm} key={label}>
        {label}
      </motion.h3>
      <motion.div {...ANIMATION_BY_KEY.leftSm} key={value}>
        {value}
      </motion.div>
    </Flex>
  )
}

const AddTryoutReceipt = ({
  tryoutFeeId,
  student,
  sessionId,
  isPaid,
}: {
  tryoutFeeId: Uuid | undefined
  student: StudentConstructor | null
  sessionId?: Uuid
  isPaid: boolean
}) => {
  const form = Form.useFormInstance()
  const theme = useThemeContext()

  let sessionLabel = "..."
  let classLabel = "..."

  const session = useOneDoc<ClassSessionConstructor>(
    classSessionAtoms,
    DOC_TYPES.CLASS_SESSIONS,
    sessionId
  )
  const [classId, index] = sessionId ? getSessionInfoFromId(sessionId) : []
  const classObj = useOneDoc<ClassConstructor>(
    classAtoms,
    DOC_TYPES.CLASSES,
    classId
  )

  if (session && classObj && index != null) {
    const { name, isSummerCamp } = classObj
    const { eventStart } = session
    const skipValue = isSummerCamp ? "Day" : "Week"

    sessionLabel = `${dayjs(eventStart).format(STANDARD_DATE_FORMAT)} - ${name} - ${skipValue} ${index + 1}`
    classLabel = `${name} - ${isSummerCamp ? "Day" : "Week"} ${index + 1}`
  }

  // Calculate values for hidden inputs
  const studentName = student ? `${student.firstName} ${student.lastName}` : ""
  const studentDob = student ? (formatDob(student.dob) ?? UNKNOWN_DOB) : ""

  // Update hidden form fields when values change
  useEffect(() => {
    form.setFieldsValue({
      receipt: {
        studentName,
        studentDob,
        classLabel,
      },
    })
  }, [form, studentName, studentDob, classLabel])

  return (
    <Flex col gap={20}>
      <ReceiptItem
        label="Student"
        value={
          student
            ? `${student.firstName} ${student.lastName}`
            : "Select a student"
        }
      />

      <ReceiptItem label="DOB" value={student ? `${studentDob}` : "..."} />

      <Flex col gap={10}>
        <ReceiptItem label="Session" value={sessionLabel} />
      </Flex>

      <Flex col gap={10}>
        <motion.h3 {...ANIMATION_BY_KEY.leftSm} key="tryout-fees">
          Tryout fees
        </motion.h3>
        <Flex col gap={4}>
          {!!tryoutFeeId ? (
            <AnimateInFlex
              animation={ANIMATION_BY_KEY.leftSm}
              animationKey={tryoutFeeId}
              key={`${tryoutFeeId}-key`}
            >
              ${feesById[tryoutFeeId].price} - {feesById[tryoutFeeId].label}
            </AnimateInFlex>
          ) : (
            <AnimateInFlex
              animation={ANIMATION_BY_KEY.leftSm}
              animationKey="no-fees"
              style={{ color: theme.error }}
            >
              Add fees to send a receipt.
            </AnimateInFlex>
          )}
        </Flex>
      </Flex>

      <ReceiptItem label="Paid" value={`${isPaid ? "Yes" : "No"}`} />

      {/* Hidden receipt fields for form submission */}
      <Form.Item name={["receipt", "studentName"]} hidden>
        <Input />
      </Form.Item>
      <Form.Item name={["receipt", "DOB"]} hidden>
        <Input />
      </Form.Item>
      <Form.Item name={["receipt", "classLabel"]} hidden>
        <Input />
      </Form.Item>
    </Flex>
  )
}

export default AddTryoutReceipt
